import { FC, useMemo } from 'react'
import * as cn from 'classnames'
import styles from 'components/features/coaching/advisorPage/MeetingDetails/MeetingDetails.styl'
import { AdvisorHeaderWith } from 'components/features/coaching/advisor/AdvisorHeaderWith/AdvisorHeaderWith'
import { MeetingStatus } from 'components/features/coaching/advisorPage/types/interfaces'
import moment from 'moment'
import { AdvisorTopics, topicsMap } from 'components/features/coaching/advisorMeeting/fixtures/requestQuestions'
import { EditInPlaceInputController } from 'components/atoms/inputs/EditInPlaceInput/EditInPlaceInput'
import { AdvisorUid, RawAdvisorReview } from 'components/features/coaching/advisor/types/advisor'
import { AdvisorRequestNotes } from 'components/features/coaching/advisorMeeting/types/requests'
import { MEETING_STATUS_MAP } from 'components/features/coaching/advisorMeeting/types/constants'
import { StarsPicker } from 'components/features/coaching/advisorReviewPage/StarsPicker/StarsPicker'

type MeetingDetailsProps = {
  advisorUid: AdvisorUid
  status?: MeetingStatus
  duration?: number
  topics?: AdvisorTopics[]
  notes?: AdvisorRequestNotes
  coach_notes?: string[]
  review?: RawAdvisorReview | null
  timeslot?: string
  is_cancellable?: boolean
}
export const MeetingDetails: FC<MeetingDetailsProps> = (props) => {
  const {
    advisorUid,
    status,
    topics,
    notes,
    coach_notes,
    review,
    timeslot,
    duration,
  } = props

  const topicsChild = useMemo(() => {
    if (!topics) {
      return null
    }
    return (
      <>
        <hr />
        <div>
          <dt className={ styles.key }>Topics</dt>
          <dd data-qa-type='topics'>{ topics.map((topicSlug) => topicsMap[topicSlug]).join(', ') }</dd>
        </div>
      </>
    )
  }, [topics])

  const dateChild = useMemo(() => moment(timeslot).format('dddd, MMMM Do'), [timeslot])
  const timeChild = useMemo(() => moment(timeslot).format('h:mm A'), [timeslot])

  const reviewSection = useMemo(() => !!review && (
    <>
      <hr />
      <div>
        <dt className={ styles.key }>Your Rating</dt>
        <dd><StarsPicker value={ review.rating } small /></dd>
      </div>
      <hr />
      <div className={ styles.vertical }>
        <dt className={ styles.key }>Your Review</dt>
        <dd className={ styles.note }>{ review.text }</dd>
      </div>
    </>
  ), [review])

  const durationSection = useMemo(() => !!duration && (
    <>
      <hr />
      <div>
        <dt className={ styles.key }>Duration</dt>
        <dd data-qa-type='duration'>{ duration } Minutes</dd>
      </div>
    </>
  ), [duration])

  const yourNotesSection = useMemo(() => !!notes && (
    <>
      <hr />
      <div className={ styles.vertical }>
        <dt className={ styles.key }>Your Note</dt>
        <dd className={ styles.note } data-qa-marker='your-notes'>
          <EditInPlaceInputController
            name='notes'
            withBg
            disabled={ ['completed', 'cancelled', 'scheduled', 'missed'].includes(status as string) }
          />
        </dd>
      </div>
    </>
  ), [notes, status])

  const coachNotesSection = useMemo(() => !!coach_notes && coach_notes.length > 0 && (
    <>
      <hr />
      <div className={ styles.vertical }>
        <dt className={ styles.key }>Your Coach's Notes</dt>
        <dd className={ styles.note } data-qa-type='coach-notes'>
          {
            coach_notes.map((coach_note, i) => (
              <p key={ i }>{ coach_note }</p>
            ))
          }
        </dd>
      </div>
    </>
  ), [coach_notes])

  const statusSection = useMemo(() => !!status && (
    <>
      <hr />
      <div>
        <dt className={ styles.key }>Status</dt>
        <dd className={ cn(styles.status, styles[status]) } data-qa-type='session-status'>{ MEETING_STATUS_MAP[status] }</dd>
      </div>
    </>
  ), [status])

  const timeslotSection = useMemo(() => !!timeslot && (
    <>
      <hr />
      <div>
        <dt className={ styles.key }>Date</dt>
        <dd data-qa-type='date'>{ dateChild }</dd>
      </div>
      <hr />
      <div>
        <dt className={ styles.key }>Time</dt>
        <dd data-qa-type='time'>{ timeChild }</dd>
      </div>
    </>
  ), [timeslot, dateChild, timeChild])

  return (
    <div className={ styles.meetingPreview } data-qa-marker='meeting-preview'>
      <dt className={ styles.key }>Coach</dt>
      <div className={ styles.advisorWrapper }>
        <AdvisorHeaderWith uid={ advisorUid } showRating qaMarker='advisor-info' />
      </div>
      <dl data-qa-marker='meeting-details'>
        { statusSection }
        { timeslotSection }
        { durationSection }
        { topicsChild }
        { yourNotesSection }
        { coachNotesSection }
        { reviewSection }
      </dl>
    </div>
  )
}
